import appstore from "../../appStore";
import { Dispatch } from "../Dispatch";
import { Api } from "../../Services/Api";
import { MapView } from "../../widgets/NavBar/TabEntities";
import { VehicleOption, DataLoadingStatus } from '../Condition/Redux/ConditionEntities';
import services, { ServiceKind } from '../../utils/service-vehicles/ServiceMetadata';
import { byTaxiServiceId } from '../../utils/service-vehicles/LookupServices';
import { RecentTripButtonKind, BookingTabKind, BookingFormKind } from '../UILogicControl/UILogicControlEntities';
import { ApiGeoPoint } from '../../Services/BookingEntities';
import { GetPlaceIdByAddress, ConvertToPlaceResult, LoadGooglePlaceDetails, ApplyPickupLocationSelection, ApplyDropoffLocationSelection } from './BookingLocationHelper';
import { AsyncUpdateOutcome } from "../Condition/AsyncUpdateOutcome";
import { PaymentCardErrorType, PayDriverOption } from "../Payment/PaymentEntities";
import { SimpleUserProfile } from "../User/ProfileEntitiesV2";
import { TemplateToBookingAccountInfo } from "../BookingTemplate/BookingTemplateEntities";
import { BookingTemplate } from "../BookingTemplate/BookingTemplateEntities";
import { AccountBookingPayload, PlaceResult } from "./BookingEntities";
import { ShowDialogSimpleErrorMessage } from "../../widgets/general-error-message/ErrorMessagingHelper";
import { WellKnownMessageKind } from "../Utils/ErrorMessages";
import { LogEvent } from '../../utils/LogEvent';
import { PopulateConditionListWithDefaultVehicles } from "../Condition/PopulateConditions";
import { ConvertToPaymentOption } from "../Payment/PaymentHandler";
import { BookingInfo, SelectedBookingDetails } from "../MyBookings/MyBookingEntities";
import { FeatureFlags } from "../../Config/FeatureFlags";
import { ParseAndStoreDropoffContactNumber, UpdateDropoffContactAndName } from "./Parcel/BookingContactUtil";
import { CheckPhone } from "../PhoneNumber/CheckPhone";
import { PreviewFeatureId } from "../Features/FeatureEntities";
import { LoadCuratedLocations } from "../CuratedLocations/CuratedLocations";

/**
 * Populate the pickup and dropoff driver notes
 * Populate the account details
 * Populate the pickup and dropoff address
 * Populate the cab details and estimate fare
 * Populate the pickup and dropoff contact details
 * Select the type of Booking Form (Taxi or Parcel Mode)
 */
export async function RecentTripSelection(recentTripData: SelectedBookingDetails) {

    const BookingDetails = recentTripData.BookingDetails;
       
    ClearExistingDetails();

    PopulateAccountDetails(BookingDetails);

    // Use TaxiServiceId for BookingV2
    if (FeatureFlags.BookingApiV2) {
        const taxiServiceId = BookingDetails.ServiceOffering?.TaxiServiceId;
        PopulateCabDetailsByTaxiServiceId(taxiServiceId);
    }

    // Select the type of Booking Form.
    SetTaxiOrParcelMode();

    if (!FeatureFlags.GlobalBooker) {
        const locationDetails = await PopulateMiscBookingDetails(recentTripData);

        if (locationDetails) {
            await PopulateAddresses(locationDetails, recentTripData);
        }
    }
    else {
        // Driver notes
        if (BookingDetails.NotesToDriver) {

            const notes = CleanDriverNoteText(BookingDetails.NotesToDriver);
            
            Dispatch.Booking.DriverNotes(0, notes);
        }        

        // No place ID from GlobalBooker API.
        const bookingPlaceIds: BookingPlaceIds = {
            PickupPlaceId: null,
            DropoffPlaceId: null
        };

        await PopulateAddresses(bookingPlaceIds, recentTripData);
    }

    // Populate Passenger Name
    PopulatePassengerName(BookingDetails.PassengerName);

    // Populate passenger contact number.
    ParseAndStoreContactNumber(BookingDetails.CustomerPhone);
}

export async function BookingTemplateSelection(selectedTemplate: BookingTemplate) {
    
    // Change to NewBooking Tab
    Dispatch.UILogicControl.OnBookingTabSelectionChange(BookingTabKind.NewBooking);

    // Navigate to MapView
    Dispatch.Tab.SelectItem(MapView);

    // Populate account details
    PopulateAccountDetailsFromExistingSource(selectedTemplate);
    
    // Switch OFF the strict validation mode
    Dispatch.UILogicControl.OnIsStrictValidationModeOnBookingFormChange(false);

    ClearExistingDetails();

    const pickup = selectedTemplate.Locations[0];

    //#region Populate pickup address 

    if (pickup.GoogleMapsPlaceId) {

        await PopulatePickupAddress(pickup.GoogleMapsPlaceId, pickup.DisplayText ?? null);
        Dispatch.CuratedLocations.CurrentlyActiveLocationIndex(0);
        await LoadCuratedLocations(0);
    }
    else {
        const { LoadingStatus, FareLoadStatus } = appstore.getState().condition;

        Dispatch.Condition.ConditionDataLoadStatus({ ...LoadingStatus, Status: DataLoadingStatus.Idle });

        Dispatch.Condition.FareLoadStatus({ ...FareLoadStatus, LastInput: null });

        // Populate the condition list with default values, inorder to render the selected vehicle
        PopulateConditionListWithDefaultVehicles();
    }
    
    //#endregion

    // Populate cab details (templates use V1 conditionId)    
    PopulateCabDetailsByConditionId(selectedTemplate.Conditions);

    // Select the type of Booking Form.
    SetTaxiOrParcelMode();

    //#region  Populate dropoff address 

    const dropoff = selectedTemplate.Locations[1];

    if (dropoff.GoogleMapsPlaceId) {
        await PopulateDropoffAddress(dropoff.GoogleMapsPlaceId, dropoff.DisplayText ?? null);
        Dispatch.CuratedLocations.CurrentlyActiveLocationIndex(1);
        await LoadCuratedLocations(1);
    }
    
    //#endregion

    // #region Driver Notes

    // for backwards compatibility, first check the availability of template.DriverNotes and populate it to PickupNotes field (obsolete implementation). 
    // If null, check the availability of first location's driver notes and assign to pickup notes if not null (new implementation)
    if (selectedTemplate.DriverNotes) {
        Dispatch.Booking.DriverNotes(0, selectedTemplate.DriverNotes);
    }
    else if (pickup.DriverNotes) {
        Dispatch.Booking.DriverNotes(0, pickup.DriverNotes);
    }

    // second location of the template is dropoff location (currently)
    if (dropoff.DriverNotes) {
        Dispatch.Booking.DriverNotes(1, dropoff.DriverNotes);
    }

    // #endregion
    
    // Populate passenger name
    if (pickup.ContactName) Dispatch.Booking.ContactName(0, pickup.ContactName);

    // Populate passenger contact number
    ParseAndStoreContactNumber(pickup.ContactPhone);

    if (dropoff.ContactName) Dispatch.Booking.ContactName(1, dropoff.ContactName);

    if (dropoff.ContactPhone) ParseAndStoreDropoffContactNumber(dropoff.ContactPhone);

    // Populate payment card
    PopulatePaymentMethod(false, selectedTemplate.PaymentCardId);

    LogEvent.BookingTemplateSelected(selectedTemplate);
}

/** Clear any existing state before applying a favourite or recent booking. */
function ClearExistingDetails() {
    
    // Inorder to update the store with the latest pickup and dropoff addresses, clear the previously stored addresses
    ClearAddresses();

    Dispatch.Condition.ClearFareEstimate();
    Dispatch.UILogicControl.ClearSatssError();

    // Clear the fare details and set the cab details as Next available
    Dispatch.Condition.SelectVehicle({ Service: services.any });
}

export function ClearAddresses() {

    // Clear Pickup address
    Dispatch.GoogleMap.PickupCleared();
    Dispatch.UILogicControl.OnDoesPickupInputHaveValueChange(false);

    // Clear dropoff address
    Dispatch.GoogleMap.DropoffCleared();
    Dispatch.UILogicControl.OnDoesDropoffInputHaveValueChange(false);

    const bookingLocations = appstore.getState().booking.Locations;

    for (let i = 0; i < bookingLocations.length; i++) {
        Dispatch.Booking.ClearAddress(i);
        Dispatch.Booking.ClearFavouriteAddress(i);
        Dispatch.Booking.ClearDriverNotes(i);
        Dispatch.Booking.ResetCuratedLocationAddressId(i);
    }
}

export async function PopulatePickupAddress(placeId: string | null, placeText: string | null) {

    const place = await RecoverPlaceDetailsFromIdOrText(placeId, placeText);

    if (!place) {
        Dispatch.UILogicControl.SetPickupValidity(false);
        return;
    }   
    
    const status = await ApplyPickupLocationSelection(place);
    
    const { LoadingStatus, FareLoadStatus } = appstore.getState().condition;

    // For same pickup suburbId, modify the loading and the fareload status inorder to get latest fare
    if (status === AsyncUpdateOutcome.InputsUnchanged) {
        Dispatch.Condition.ConditionDataLoadStatus({ ...LoadingStatus, Status: DataLoadingStatus.Idle });
        Dispatch.Condition.FareLoadStatus({ ...FareLoadStatus, LastInput: null });
    }

    Dispatch.UILogicControl.OnDoesPickupInputHaveValueChange(true);    
}

export async function PopulateDropoffAddress(placeId: string | null, placeText: string | null) {
   
    const place = await RecoverPlaceDetailsFromIdOrText(placeId, placeText);

    if (!place) {        
        Dispatch.UILogicControl.SetDropoffValidity(false);
        return;
    }

    await ApplyDropoffLocationSelection(place, 1);    
    
    Dispatch.UILogicControl.OnDoesDropoffInputHaveValueChange(true);
}

/**
 * The caller should provide either placeId or Text.
 */
async function RecoverPlaceDetailsFromIdOrText(placeId: string | null, placeText: string | null): Promise<PlaceResult | null> {

    // resolve place ID
    if (!placeId) {

        if (!placeText) return null;
        placeId = await GetPlaceIdByAddress(placeText);

        // give up
        if (!placeId) return null;
    }

    const placeResult = await LoadGooglePlaceDetails(placeId);
    if (!placeResult) return null;

    return ConvertToPlaceResult(placeResult, placeText);
}

/**
 * Loads extra details about the existing booking from the API in order to create a copy.
 * This is required because some booking details are not loaded by default, for example the payment method.
 * Only for V1 bookings (Booking API).
 * Returns the Google Maps Place IDs for the pickup and dropoff, if discovered. 
 */
async function PopulateMiscBookingDetails(RecentTripData: SelectedBookingDetails): Promise<BookingPlaceIds | null> {

    var BookingDetails = RecentTripData.BookingDetails;
    // Call the BookingLocation API to fetch driver notes and google placeid
    var serviceResult = await Api.Booking.GetMiscBookingDetails({ bookingId: BookingDetails.BookingID, hashCode: BookingDetails.AccessCode });

    if (!serviceResult.isSuccess) return null;

    const miscDetails = serviceResult.value;
    // pickup
    const pickup = miscDetails.Locations[0];
    // dropoff
    const dropoff = miscDetails.Locations[miscDetails.Locations.length - 1];

    let pickUpMisc = pickup;
    let dropOffMisc = dropoff;

    // Reverse the addresses when booking on return
    if (RecentTripData.TripType == RecentTripButtonKind.BookReturn) {
        pickUpMisc = dropoff;
        dropOffMisc = pickup;
    } 

    const pickupNotes = CleanDriverNoteText(pickUpMisc.remark);
    Dispatch.Booking.DriverNotes(0, pickupNotes);

    const dropoffNotes = CleanDriverNoteText(dropOffMisc.remark);
    Dispatch.Booking.DriverNotes(1, dropoffNotes);

    UpdateDropoffContactAndName(dropOffMisc.contactDetails.name, dropOffMisc.contactDetails.phoneNumber);

    // payment card
    const cardId = miscDetails.Payment?.CardId;
    PopulatePaymentMethod(RecentTripData.BookingDetails.TrackingInfo.WasJitPreauthFailure, cardId);

    const bookingPlaceIds: BookingPlaceIds = {
        PickupPlaceId: pickup.googlePlaceId,
        DropoffPlaceId: dropoff.googlePlaceId,
    };

    return bookingPlaceIds;
}

function PopulateAccountDetails(BookingDetails : BookingInfo) {
    
    // Clear previously selected account
    Dispatch.Booking.ClearAccountDetails();
    
    const { AccountNumber, OrderNumber, ContactName } = BookingDetails;

    const { UserProfile } = appstore.getState().authentication;

    if (!UserProfile) return;

    const accountInfo: TemplateToBookingAccountInfo = { AccountNumber: AccountNumber, 
                                                        FileName: ContactName, 
                                                        OrderNumber: OrderNumber };
    
    ValidateAndPopulateAccountAgainstUserProfile(UserProfile, accountInfo);
}

/**
 * Find the account in user profile from specified account number which is derived from booking history and favourite;
 * And assign into booking payload, if there is any;
 * 
 * This function also validate account related information.
 * Validation rule:
 * https://cabcharge.atlassian.net/wiki/spaces/CA/pages/999260447/Book+from+Favourite
 */
export function ValidateAndPopulateAccountAgainstUserProfile(userProfile : SimpleUserProfile, accountInfo: TemplateToBookingAccountInfo) {

    // Populate the account list
    const accountsList = userProfile.Accounts;

    /**
     * Account toggle was off when the favourite or recent trip was made
     */
    if (!accountInfo.AccountNumber) {
        Dispatch.Booking.SetBookOnAccount(false);
        return;
    }

    /**
     * No account linked to this user, but there is a specified account from booking templates/recents/booking history
     */
    if (accountsList.length === 0) {
        ShowDialogSimpleErrorMessage(WellKnownMessageKind.AccountValidationFailure); 
        return;
    }

    /**
     * New business rule:
     * As long as, there are accounts linked to user profile, account toggle is always on;
     *   1) Found matching account: show account information
     *   2) Specified account not found: show "Please select account".
     */
    const bookingOnAccount = true;

    const accIndex = accountsList.findIndex(x => x.AccountNumber == accountInfo.AccountNumber);
        
    // When booking from recent, populate the account details used previously for booking
    if(accIndex >= 0) {
        const account = accountsList[accIndex];

        const accPayload: AccountBookingPayload = {
            FileNumber: accountInfo.FileName,
            OrderNumber: account.IsOrderNumberRequired && accountInfo.OrderNumber || "",            
            SelectedAccountIndex: accIndex,
            SelectedAccount: account                
        }

        /**
         * Order number has value from booking template/recent/booking history, but not required any more from profile for the same account;
         * Ignore, if IsDVA is true.
         */
        if (!!accountInfo.OrderNumber && !account.IsOrderNumberRequired && !account.IsDVA) ShowDialogSimpleErrorMessage(WellKnownMessageKind.AccountValidationFailure); 

        // Set the index of the account, to be selected in the accounts dropdown
        Dispatch.Booking.AccountDetails(accPayload);
    }

    // Specified account cannot be found in account list linked to user
    Dispatch.UILogicControl.SpecifiedAccountNotFound(accIndex < 0);
    // Set the booking on accounts toggle button
    Dispatch.Booking.SetBookOnAccount(bookingOnAccount);
}

/**
 * Populate the pickup and dropoff address using the location text stored in database
 */
async function PopulateAddresses(bookingPlaceIds: BookingPlaceIds, recentTripData: SelectedBookingDetails) {

    const tripDetail: BookingInfo = recentTripData.BookingDetails;

    const pickupLocation = tripDetail.Locations[0];
    let pickup: PlaceRecoveryParts = {
        Geopoint: pickupLocation.GeoPoint,
        PlaceId: bookingPlaceIds.PickupPlaceId,
        PlaceText: pickupLocation.AddressText,
    };

    // currently this is the last location. but this entire logic will be updated when multi-stop booking creation is implemented.
    const destinationLocation = tripDetail.Locations[tripDetail.Locations.length - 1];
    let dropoff: PlaceRecoveryParts = {
        Geopoint: destinationLocation.GeoPoint,
        PlaceId: bookingPlaceIds.DropoffPlaceId,
        PlaceText: destinationLocation.AddressText,
    };

    // Reverse the addresses when booking on return
    if (recentTripData.TripType == RecentTripButtonKind.BookReturn) {

        const temp = pickup;
        pickup = dropoff;
        dropoff = temp;
    }

    Dispatch.UILogicControl.SetPickupValidity(isAddressValid(pickup));
    Dispatch.UILogicControl.SetDropoffValidity(isAddressValid(dropoff));

    // pickup
    if (AddressHasSpecificLocation(pickup)) {
        await PopulatePickupAddress(pickup.PlaceId, pickup.PlaceText);   
    }

    // Populate and save the dropoff address if present
    if (AddressHasSpecificLocation(dropoff)) {
        await PopulateDropoffAddress(dropoff.PlaceId, dropoff.PlaceText);
    }
}

/** Intermediate entity to help us recover a Google Place from other data */
interface PlaceRecoveryParts {
    Geopoint: ApiGeoPoint | null,
    PlaceId: string | null,
    PlaceText: string | null,
}

/**
 * True if the booking location seems have a well defined location.
 */
function AddressHasSpecificLocation(address: PlaceRecoveryParts): boolean {

    if (address.PlaceId) return true;

    // this is testing for a non-zero value too. Booking API generates 0 instead of null.
    if ((address.Geopoint) && (address.Geopoint.Latitude)) return true;

    return false;
}

/** Check if the existing address is valid (can be populated in the input field). */
function isAddressValid(address: PlaceRecoveryParts) {
    if (address.PlaceId) return true;
    if (address.Geopoint) return true;

    // KH: this is existing code and it makes no sense. This represents an empty dropoff address.
    if (address.PlaceText === "To be confirmed") return true;

    return false;
}

/**
 * Apply selection and collapse UI.
 */
function ApplyCabSelection(selectedVehicle: VehicleOption) {
  Dispatch.Condition.SelectVehicle(selectedVehicle);
  Dispatch.Condition.CollapseVehicleSelectorUI();
}

/**
 * Populate vehicle/cab details from V2 booking (ServiceOfferings API).
 *
 * @param taxiServiceId - The TaxiServiceId from V2 ServiceOffering API (e.g., 9 for Silver Exec, 10 for Silver Exec XL)
 */
/**
 * Populate vehicle/cab details from V2 booking (ServiceOfferings API).
 *
 * @param taxiServiceId - The TaxiServiceId from V2 ServiceOffering API (e.g., 9 for Silver Exec, 10 for Silver Exec XL)
 */
function PopulateCabDetailsByTaxiServiceId(taxiServiceId: number | undefined | null) {
    const { ConditionList, SelectedCondition } = appstore.getState().condition;

    if (!ConditionList || ConditionList.length === 0) return;

    let selectedVehicle: VehicleOption = { ...SelectedCondition, Service: services.any };

    // If taxiServiceId is null or undefined, default to NextAvailable
    if (!taxiServiceId) {
        ApplyCabSelection(selectedVehicle);
        return;
    }

    // Use byTaxiServiceId helper to validate service metadata exists (optional)
    const serviceMetadata = byTaxiServiceId(taxiServiceId);

    // Find matching vehicle in ConditionList by TaxiServiceId (V2)
    const matchingVehicle = ConditionList.find(
        (vehicle) =>
            vehicle.ApiVehicle &&
            vehicle.ApiVehicle.ApiVersion === 2 &&
            vehicle.ApiVehicle.ApiId === taxiServiceId
    );

    if (matchingVehicle) {
        selectedVehicle = matchingVehicle;
    } else if (serviceMetadata) {
        // Service exists but not in available vehicles - show error
        ShowDialogSimpleErrorMessage(WellKnownMessageKind.InserviceableVehicle);
    }

    ApplyCabSelection(selectedVehicle);
}

/**
 * Populate vehicle/cab details from V1 booking (legacy Booking API).
 *
 * @param conditionId - The CarConditionID from V1 Booking API (legacy)
 */
/**
 * Populate vehicle/cab details from V1 booking (legacy Booking API).
 *
 * @param conditionId - The CarConditionID from V1 Booking API (legacy)
 */
function PopulateCabDetailsByConditionId(conditionId: number | undefined) {
    const { ConditionList, MaxiTaxiLookUp, SelectedCondition } = appstore.getState().condition;

    if (!ConditionList || ConditionList.length === 0) return;

    let selectedVehicle: VehicleOption = { ...SelectedCondition, Service: services.any };

    // The conditionId is empty for the Next Available taxi
    if (conditionId) {
        const index = ConditionList.findIndex((x) => x.ApiVehicle && x.ApiVehicle!.ApiId == conditionId);

        if (index >= 0) {
            selectedVehicle = ConditionList[index];
        } else {
            if (MaxiTaxiLookUp) {
                // Retrieve Maxi taxi details
                const condition = Object.values(MaxiTaxiLookUp).find((x) => x.ApiId === conditionId);

                if (condition) {
                    // Maxi Taxi do not have the Condition property, so can't use the ID to retrieve its details
                    const maxiVehicle = ConditionList.find((x) => x.Service.isMaxiTaxi);

                    if (maxiVehicle) {
                        selectedVehicle = {
                            ...selectedVehicle,
                            Service: { ...maxiVehicle.Service, short: condition.Description },
                            ApiVehicle: condition,
                        };
                    }
                }
            }

            // Show error message dialog incase of inserviceable vehicle
            if (selectedVehicle.Service === services.any) {
                ShowDialogSimpleErrorMessage(WellKnownMessageKind.InserviceableVehicle);
            }
        }
    }

    ApplyCabSelection(selectedVehicle);
}

export function ParseAndStoreContactNumber(phoneNumber: string | undefined): void {

    Dispatch.Verification.ClearContactNumberError();

    // Set default country information for empty contact number
    if (!phoneNumber) {
        Dispatch.Booking.ClearContactPhone(0);
        return;
    }

    const result = CheckPhone.FromRawNumber(phoneNumber);

    if (result.IsValid) {
        Dispatch.Booking.ContactPhone(0, result.Value);
    }
    else {
        Dispatch.Booking.ClearContactPhone(0);
    }
}

/**
 * Populate the payment method with the specified payment card 
 * If the card is not found, then display error and default the payment option to "Paying the Driver directly"
 */
function PopulatePaymentMethod(wasJitPreauthFailure: boolean, paymentCardId: string | undefined) {    

    // If JIT preauth failed in the selected booking, display an error message and do not select the card.
    if (wasJitPreauthFailure) {
        Dispatch.Payment.CardErrorMessage(PaymentCardErrorType.SelectedPaymentMethodError);

        // this should be for all the Fixed Price bookings. But we don't have that data currently and this is the only place we know that the booking was a fixed price booking. This wouldn't matter since we will implement 'default to fixed price' feature soon and then all the bookings will be default to FP.
        Dispatch.Condition.SetPriceGuarantee();
        return;
    }

    let paymentOption = PayDriverOption;
    
    Dispatch.Payment.ClearCardError();

    if (paymentCardId) {
        
        const cardsList = appstore.getState().payment.AllCards;
        const paymentCard = cardsList.find(card => card.CardId === paymentCardId);

        if (paymentCard) {
            paymentOption = ConvertToPaymentOption(paymentCard);
        } else {
            // Card not found in the list of cards belonging to the user.
            Dispatch.Payment.CardErrorMessage(PaymentCardErrorType.CardNotFound);
        }
    }

    // If no payment option found in the selected booking, default to 'Paying the Driver directly'
    Dispatch.Booking.PaymentMethod(paymentOption);
}

function PopulatePassengerName(passengerName: string | undefined) {
    if (passengerName) {
        Dispatch.Booking.ContactName(0, passengerName);
        LogEvent.OnAddingPassengerName();
    }
}

/**
 * Populate account details from booking template or from an old booking (quick book tab or history)
 */
function PopulateAccountDetailsFromExistingSource(BookingTemplate: BookingTemplate) {
    // Clear previously selected account
    Dispatch.Booking.ClearAccountDetails();

    const { UserProfile } = appstore.getState().authentication;

    if (!UserProfile) return;

    const AccountInfo: TemplateToBookingAccountInfo = {
        AccountNumber: BookingTemplate.AccountNumber,
        FileName: "",
        OrderNumber: BookingTemplate.AccountReferenceText
    };

    ValidateAndPopulateAccountAgainstUserProfile(UserProfile, AccountInfo);
}

/** Extract the driver notes entered by the user. i.e.: remove predefined driver notes added automatically to the booking (for account bookings). */
function CleanDriverNoteText(fullNote: string): string {
    // Check for the predefined driver notes 
    const index = fullNote.lastIndexOf("--");

    // Remove the prefined driver notes
    if (index > 0) return fullNote.slice(index + 2).trim();

    return fullNote;
}

/** When the parcel widget is available, set taxi vs parcel mode based on the type of booking that has been loaded. */
function SetTaxiOrParcelMode() {
    const state = appstore.getState();

    if (state.features.EnabledPreviews[PreviewFeatureId.ParcelDeliveryWidget]) {

        const isParcel = state.condition.SelectedCondition.Service.kind === ServiceKind.Parcel;

        const formMode = isParcel ? BookingFormKind.ParcelBooking : BookingFormKind.PassengerBooking;

        Dispatch.UILogicControl.ActiveBookingForm(formMode);
    }
}

/** Best effort at getting Google maps place ID for pickup and dropoff. */
interface BookingPlaceIds {

    /** Placeid for Pickup address */
    PickupPlaceId: string | null,

    /** Placeid for Dropoff address */
    DropoffPlaceId: string | null
}